// src/components/VanTransport.tsx
import React, { useState } from 'react';
import styled from 'styled-components';

// Styled Components
const Section = styled.section`
    padding: 60px 0;
    background: #f8fafc;
`;

const Container = styled.div`
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
`;

const SectionHeader = styled.div`
    text-align: center;
    margin-bottom: 3rem;
`;

const Title = styled.h2`
    font-size: 2.5rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: #1a1a1a;
    
    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const Subtitle = styled.p`
    font-size: 1.1rem;
    color: #666;
    line-height: 1.6;
    max-width: 600px;
    margin: 0 auto;
`;

const Content = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3rem;
    align-items: center;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 2rem;
    }
`;

// Gallery Styles
const Gallery = styled.div`
    border-radius: 12px;
    overflow: hidden;
`;

const MainImage = styled.div`
    height: 300px;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 1rem;
    
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
    }
`;

const Thumbnails = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 0.5rem;
`;

const Thumbnail = styled.div<{ active: boolean }>`
    height: 80px;
    border-radius: 8px;
    overflow: hidden;
    cursor: pointer;
    border: 2px solid ${props => props.active ? '#667eea' : 'transparent'};
    opacity: ${props => props.active ? 1 : 0.7};
    
    &:hover {
        opacity: 1;
    }
    
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

// Features Styles
const Features = styled.div``;

const FeaturesTitle = styled.h3`
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    color: #1a1a1a;
`;

const FeaturesList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
`;

const FeatureItem = styled.li`
    padding: 1rem 0;
    border-bottom: 1px solid #e5e7eb;
    color: #666;
    font-size: 1rem;
    
    &:last-child {
        border-bottom: none;
    }
    
    &:before {
        content: '•';
        color: #667eea;
        font-weight: bold;
        margin-right: 0.5rem;
    }
`;

// Component
const VanTransport: React.FC = () => {
    const [currentImage, setCurrentImage] = useState(0);

    const vanImages = [
        '/van1.jpeg',
        '/van2.jpeg',
        '/van3.jpeg',
        '/van4.jpeg'
    ];

    const features = [
        "Comfortable seating for 2-7 passengers",
        "Air conditioning and heating",
        "Ample luggage space",
        "Professional drivers",
        "Regular maintenance and safety checks",
        "Clean and well-maintained vehicles"
    ];

    return (
        <Section id="transport">
            <Container>
                <SectionHeader>
                    <Title>Our Transport</Title>
                    <Subtitle>
                        Travel comfortably in our modern vans, designed for small groups and scenic journeys
                    </Subtitle>
                </SectionHeader>

                <Content>
                    {/* Gallery Section */}
                    <Gallery>
                        <MainImage>
                            <img
                                src={vanImages[currentImage]}
                                alt={`Our van ${currentImage + 1}`}
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.backgroundColor = '#e5e7eb';
                                    target.style.display = 'flex';
                                    target.style.alignItems = 'center';
                                    target.style.justifyContent = 'center';
                                    target.style.color = '#666';
                                    target.innerHTML = `Van Image ${currentImage + 1}`;
                                }}
                            />
                        </MainImage>

                        <Thumbnails>
                            {vanImages.map((image, index) => (
                                <Thumbnail
                                    key={index}
                                    active={index === currentImage}
                                    onClick={() => setCurrentImage(index)}
                                >
                                    <img
                                        src={image}
                                        alt={`Van thumbnail ${index + 1}`}
                                        onError={(e) => {
                                            const target = e.target as HTMLImageElement;
                                            target.style.backgroundColor = '#e5e7eb';
                                            target.style.display = 'flex';
                                            target.style.alignItems = 'center';
                                            target.style.justifyContent = 'center';
                                            target.style.color = '#666';
                                            target.innerHTML = `${index + 1}`;
                                        }}
                                    />
                                </Thumbnail>
                            ))}
                        </Thumbnails>
                    </Gallery>

                    {/* Features Section */}
                    <Features>
                        <FeaturesTitle>Van Features</FeaturesTitle>
                        <FeaturesList>
                            {features.map((feature, index) => (
                                <FeatureItem key={index}>
                                    {feature}
                                </FeatureItem>
                            ))}
                        </FeaturesList>
                    </Features>
                </Content>
            </Container>
        </Section>
    );
};

export default VanTransport;